import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Upload, Loader2, CheckCircle, ArrowRight, FileSpreadsheet, Sparkles } from 'lucide-react';

const API = (window.location.origin || '') + '/api';

const CSVColumnMapper = ({ isOpen, onClose, getAuthHeaders, onImportComplete }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Map, 3: Import
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Preview data
  const [columns, setColumns] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [suggestedMappings, setSuggestedMappings] = useState({});
  const [targetFields, setTargetFields] = useState([]);
  
  // User mappings
  const [mappings, setMappings] = useState({});
  const [defaultCategory, setDefaultCategory] = useState('PMP');
  const [defaultDifficulty, setDefaultDifficulty] = useState('medium');
  const [duplicateAction, setDuplicateAction] = useState('skip'); // skip, update, create
  
  // Import result
  const [importResult, setImportResult] = useState(null);

  const resetState = () => {
    setStep(1);
    setFile(null);
    setColumns([]);
    setSampleRows([]);
    setTotalRows(0);
    setSuggestedMappings({});
    setMappings({});
    setImportResult(null);
    setDuplicateAction('skip');
  };

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
    }
  }, []);

  const handlePreview = async () => {
    if (!file) return;
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API}/questions/preview-csv`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setColumns(data.columns || []);
        setSampleRows(data.sample_rows || []);
        setTotalRows(data.total_rows || 0);
        setSuggestedMappings(data.suggested_mappings || {});
        setTargetFields(data.target_fields || []);
        
        // Initialize mappings with suggestions (use 'skip' for unmapped columns)
        const initialMappings = {};
        data.columns.forEach(col => {
          initialMappings[col] = data.suggested_mappings[col] || 'skip';
        });
        setMappings(initialMappings);
        
        setStep(2);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to preview CSV');
      }
    } catch (error) {
      toast.error('Failed to preview CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    // Validate required mappings
    const hasQuestionText = Object.values(mappings).includes('question_text');
    const hasCorrectAnswers = Object.values(mappings).includes('correct_answers');
    
    if (!hasQuestionText) {
      toast.error('Please map a column to "Question Text"');
      return;
    }
    
    if (!hasCorrectAnswers) {
      toast.error('Please map a column to "Correct Answer(s)"');
      return;
    }
    
    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const params = new URLSearchParams({
        mappings: JSON.stringify(mappings),
        default_category: defaultCategory,
        default_difficulty: defaultDifficulty,
        duplicate_action: duplicateAction
      });
      
      const response = await fetch(`${API}/questions/import-with-mapping?${params}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setImportResult(data);
        setStep(3);
        
        if (data.imported_count > 0) {
          toast.success(`${data.imported_count} questions imported successfully!`);
          onImportComplete?.();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to import CSV');
      }
    } catch (error) {
      toast.error('Failed to import CSV');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#0047AB]" />
            Smart CSV Import
          </DialogTitle>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-[#0047AB] text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {step > s ? <CheckCircle className="w-4 h-4" /> : s}
              </div>
              <span className={`text-sm ${step >= s ? 'text-[#0047AB] font-medium' : 'text-slate-500'}`}>
                {s === 1 ? 'Upload' : s === 2 ? 'Map Columns' : 'Done'}
              </span>
              {s < 3 && <ArrowRight className="w-4 h-4 text-slate-300 ml-2" />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-4">
            <Card className="border-dashed border-2">
              <CardContent className="p-8">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-lg font-medium mb-2">Upload your CSV file</p>
                  <p className="text-sm text-slate-500 mb-4">
                    Any CSV format works - you'll map the columns in the next step
                  </p>
                  
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label htmlFor="csv-upload">
                    <Button variant="outline" className="cursor-pointer" asChild>
                      <span>Choose CSV File</span>
                    </Button>
                  </label>
                  
                  {file && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg inline-flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-700 font-medium">{file.name}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={handlePreview}
                disabled={!file || loading}
                className="bg-[#0047AB] hover:bg-[#003380]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    Next: Map Columns
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Map Columns */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Found {totalRows} rows</strong> in your CSV. 
                Map your columns to the correct fields below. 
                Suggested mappings are pre-filled based on column names.
              </p>
            </div>

            {/* Column Mappings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Column Mapping</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {columns.map((col) => (
                  <div key={col} className="flex items-center gap-4">
                    <div className="w-1/3">
                      <p className="font-medium truncate" title={col}>{col}</p>
                      {sampleRows[0] && (
                        <p className="text-xs text-slate-500 truncate" title={sampleRows[0][col]}>
                          e.g., "{sampleRows[0][col]?.substring(0, 50) || '(empty)'}"
                        </p>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                    <div className="w-1/2">
                      <Select
                        value={mappings[col] || 'skip'}
                        onValueChange={(value) => setMappings(prev => ({ ...prev, [col]: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select target field..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px] overflow-y-auto">
                          <SelectItem value="skip">-- Do not import --</SelectItem>
                          {targetFields.map((field) => (
                            <SelectItem key={field.id} value={field.id}>
                              {field.name}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {suggestedMappings[col] && mappings[col] === suggestedMappings[col] && (
                      <Badge variant="secondary" className="text-xs">Auto</Badge>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Fallback Values - Only used when columns not mapped */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <span>Fallback Values</span>
                  <Badge variant="secondary" className="text-xs font-normal">Optional</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-4">
                  These values are only used for rows where the CSV doesn't have a value. 
                  {Object.values(mappings).includes('category') && Object.values(mappings).includes('difficulty') && (
                    <span className="text-green-600 font-medium"> Both Category and Difficulty are mapped from your CSV!</span>
                  )}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      Fallback Category
                      {Object.values(mappings).includes('category') && (
                        <Badge variant="outline" className="text-green-600 border-green-300 text-xs">Mapped</Badge>
                      )}
                    </Label>
                    <Select 
                      value={defaultCategory} 
                      onValueChange={setDefaultCategory}
                      disabled={Object.values(mappings).includes('category')}
                    >
                      <SelectTrigger className={Object.values(mappings).includes('category') ? 'opacity-50' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PMP">PMP</SelectItem>
                        <SelectItem value="CAPM">CAPM</SelectItem>
                        <SelectItem value="PSM">Scrum Master</SelectItem>
                        <SelectItem value="PSPO">Product Owner</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      Fallback Difficulty
                      {Object.values(mappings).includes('difficulty') && (
                        <Badge variant="outline" className="text-green-600 border-green-300 text-xs">Mapped</Badge>
                      )}
                    </Label>
                    <Select 
                      value={defaultDifficulty} 
                      onValueChange={setDefaultDifficulty}
                      disabled={Object.values(mappings).includes('difficulty')}
                    >
                      <SelectTrigger className={Object.values(mappings).includes('difficulty') ? 'opacity-50' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Duplicate Handling */}
            <Card className="border-orange-200 bg-orange-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <span>Duplicate Detection</span>
                  <Badge variant="outline" className="text-orange-600 border-orange-300">Important</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">
                  What should happen if a question with the same text already exists?
                </p>
                <div className="space-y-3">
                  <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    duplicateAction === 'skip' ? 'border-[#0047AB] bg-[#0047AB]/5' : 'border-slate-200'
                  }`}>
                    <input
                      type="radio"
                      name="duplicateAction"
                      value="skip"
                      checked={duplicateAction === 'skip'}
                      onChange={(e) => setDuplicateAction(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium">Skip duplicates</p>
                      <p className="text-sm text-slate-500">Don't import questions that already exist (recommended)</p>
                    </div>
                  </label>
                  
                  <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    duplicateAction === 'update' ? 'border-[#0047AB] bg-[#0047AB]/5' : 'border-slate-200'
                  }`}>
                    <input
                      type="radio"
                      name="duplicateAction"
                      value="update"
                      checked={duplicateAction === 'update'}
                      onChange={(e) => setDuplicateAction(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium">Update existing</p>
                      <p className="text-sm text-slate-500">Overwrite existing questions with new data from CSV</p>
                    </div>
                  </label>
                  
                  <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    duplicateAction === 'create' ? 'border-[#0047AB] bg-[#0047AB]/5' : 'border-slate-200'
                  }`}>
                    <input
                      type="radio"
                      name="duplicateAction"
                      value="create"
                      checked={duplicateAction === 'create'}
                      onChange={(e) => setDuplicateAction(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium">Create anyway</p>
                      <p className="text-sm text-slate-500">Import all questions even if duplicates exist (creates duplicates)</p>
                    </div>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Sample Preview */}
            {sampleRows.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Preview (First 3 rows)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          {columns.filter(col => mappings[col] && mappings[col] !== 'skip').slice(0, 5).map(col => (
                            <th key={col} className="text-left p-2 font-medium">
                              {mappings[col] || col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sampleRows.slice(0, 3).map((row, i) => (
                          <tr key={i} className="border-b">
                            {columns.filter(col => mappings[col] && mappings[col] !== 'skip').slice(0, 5).map(col => (
                              <td key={col} className="p-2 truncate max-w-[200px]" title={row[col]}>
                                {row[col]?.substring(0, 50) || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button 
                onClick={handleImport}
                disabled={importing}
                className="bg-[#0047AB] hover:bg-[#003380]"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    Import {totalRows} Questions
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Done */}
        {step === 3 && importResult && (
          <div className="space-y-4">
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Import Complete!</h3>
              <p className="text-slate-600">
                <strong>{importResult.imported_count}</strong> new questions imported
                {importResult.updated_count > 0 && (
                  <>, <strong>{importResult.updated_count}</strong> updated</>
                )}
              </p>
              
              {importResult.skipped_count > 0 && (
                <p className="text-blue-600 mt-2">
                  {importResult.skipped_count} duplicates skipped
                </p>
              )}
              
              {importResult.error_count > 0 && (
                <p className="text-orange-600 mt-2">
                  {importResult.error_count} rows had errors
                </p>
              )}
            </div>

            {/* Duplicates Found Info */}
            {importResult.duplicates_found > 0 && (
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-lg text-blue-700">
                    {importResult.duplicates_found} Duplicate(s) Found
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-blue-600 mb-2">
                    These questions already exist in the database:
                  </p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    {importResult.duplicate_samples?.slice(0, 5).map((dup, i) => (
                      <li key={i} className="truncate">
                        Row {dup.row}: {dup.question_text}
                      </li>
                    ))}
                    {importResult.duplicates_found > 5 && (
                      <li className="text-blue-500">...and {importResult.duplicates_found - 5} more</li>
                    )}
                  </ul>
                </CardContent>
              </Card>
            )}

            {importResult.errors && importResult.errors.length > 0 && (
              <Card className="bg-orange-50">
                <CardHeader>
                  <CardTitle className="text-lg text-orange-700">Import Errors</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="text-sm text-orange-700 space-y-1">
                    {importResult.errors.slice(0, 10).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Translation Hint */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800">Next Step: Translate</p>
                  <p className="text-sm text-blue-700">
                    Your questions are now in English. Use the <strong>Smart Translation</strong> feature 
                    in the Translate tab to automatically translate them to other languages.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleClose} className="bg-[#0047AB] hover:bg-[#003380]">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CSVColumnMapper;
